import { useEffect, useMemo, useRef, useState } from "react";
import Carregando from "../componentes/Carregando";
import { useRolarAoEscolher } from "../lib/rolarPara";
import {
  ChipsDeFiltro,
  Etiqueta,
  EtiquetasDeRegime,
  GradeDoMes,
  NavegacaoDeMes,
  PontoDeSetor,
  RubricaDoDia,
  SeloDeAjuste,
  TileDeFato,
  type DiaComObrigacoes,
} from "../componentes/CalendarioUi";
import {
  REGIMES,
  SETORES,
  lerCatalogo,
  lerClientes,
  lerFeitosDoMes,
  marcar,
  marcarLote,
  rotuloDoRegime,
  type Catalogo,
  type Cliente,
  type MesDoVencimento,
  type Obrigacao,
  type Regime,
  type Setor,
} from "../lib/calendario";
import {
  chaveDoFeito,
  clientesAlcancados,
  clientesQueDevem,
  pendenciasDaCarteira,
  resumoDaCarteira,
} from "../lib/carteira";
import {
  NOMES_DOS_MESES,
  dataBr,
  ehDiaUtil,
  explicarRegra,
  gradeDoMes,
  indexarExcecoes,
  mesAnterior,
  mesSeguinte,
  montarCalendario,
  vencimentoDaObrigacao,
  type Vencimento,
} from "../lib/calendarioFiscal";
import Clientes from "./CalendarioClientes";

/**
 * O Calendário Tributário Federal dentro do hub (000106, 000109, 000110, 000112).
 *
 * ## A diferença que justifica o porte
 *
 * No app da Lovable cada obrigação é uma LINHA COM DATA: 62 delas, cobrindo maio
 * a setembro de 2026, e a última é 30/09. A ferramenta acabaria ali sem avisar —
 * outubro apareceria vazio. Aqui a obrigação guarda a REGRA, e a data é derivada;
 * 2027, 2030 e 2045 aparecem sem ninguém digitar nada.
 *
 * ## Por que a tela tem esta forma, e não a de um mês com uma lista ao lado
 *
 * Três medições de 25/08 decidiram o desenho:
 *
 * 1. **a grade estava 82% vazia.** Nos doze meses de 2026 o mês tem em média
 *    14,5 vencimentos concentrados em **5,3 dias distintos** — como
 *    protagonista, a grade gastava metade da tela para marcar cinco dias. Ela
 *    virou o MAPA, dentro da cabine, com altura fixa;
 * 2. **a tela tinha 5.224px no celular** (5,8 telas de rolagem) para 14 itens. A
 *    data era dita 15 vezes na lista mais 5 na grade; agora é dita uma vez por
 *    RUBRICA de dia — 6 vezes. É a cicatriz do IR Pro, onde 34 assuntos em dois
 *    lugares davam 8.249px e caíram para 1.120px com o mesmo conteúdo;
 * 3. **fechar o mês custava 40 trocas de modo.** A marca de feito é por
 *    (cliente, obrigação, mês), e a tela só sabia falar de um cliente por vez:
 *    40 clientes × 14 prazos = 560 tiques, com 40 leituras de rede. Agora a
 *    obrigação é a LINHA e os clientes que ainda devem são caixas dentro dela —
 *    "marcar os 12 do Simples" é um clique e UMA transação (`000112`).
 *
 * ## O que esta tela NÃO faz
 *
 * Não calcula data — `calendarioFiscal.ts` calcula, e é o mesmo módulo que o ETL
 * usou para inferir as regras. Não remonta a explicação nem o selo do ajuste:
 * saem de `explicarRegra` e da `Derivacao` que nasce junto do número, pela lição
 * da 000091. Não decide quem alcança quem: `lib/carteira.ts` decide, num lugar
 * só. Não decide acesso: a RLS devolve zero linha para quem não tem o card.
 *
 * ## Regra não confirmada aparece com letra
 *
 * 19 das 20 obrigações tiveram a regra INFERIDA das datas que o app de origem
 * mostrava — hipótese boa, não a lei. A nota do alto diz quantas faltam, e a
 * etiqueta fica em cada linha: calendário que não distingue "conferido" de
 * "deduzido" ensina o contador a confiar no que não foi verificado, e o preço de
 * um vencimento errado é multa dele.
 */
export default function CalendarioTributario() {
  const hoje = useMemo(() => new Date(), []);
  const hojeIso = useMemo(() => {
    const p = (n: number) => String(n).padStart(2, "0");
    return `${hoje.getFullYear()}-${p(hoje.getMonth() + 1)}-${p(hoje.getDate())}`;
  }, [hoje]);

  const [aba, setAba] = useState<"calendario" | "clientes">("calendario");
  const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [erro, setErro] = useState(false);

  const [mesEmFoco, setMesEmFoco] = useState<MesDoVencimento>({
    ano: hoje.getFullYear(),
    mes: hoje.getMonth() + 1,
  });
  const [diaEmFoco, setDiaEmFoco] = useState<string | null>(null);
  // Clicar num dia da grade filtra a lista de prazos, que fica abaixo dela. A
  // decisão ANTIGA era não rolar, e o motivo escrito em `CalendarioUi.tsx` era
  // que rolar exigiria cravar a altura do Topo. Não exige: `--cf-topo` existe
  // exatamente para isso, e `lib/rolarPara.ts` a lê. Victor achou o defeito na
  // tela em 28/08.
  const prazosRef = useRef<HTMLDivElement | null>(null);
  useRolarAoEscolher(prazosRef, diaEmFoco);
  const [setores, setSetores] = useState<Set<Setor>>(new Set());
  const [regimes, setRegimes] = useState<Set<Regime>>(new Set());
  const [clienteEmFoco, setClienteEmFoco] = useState<string>("");
  const [feitos, setFeitos] = useState<Set<string>>(new Set());
  const [salvando, setSalvando] = useState<string | null>(null);
  const [falhaAoMarcar, setFalhaAoMarcar] = useState<string | null>(null);
  const [carteiraInteira, setCarteiraInteira] = useState(false);
  const [mostrandoEmDia, setMostrandoEmDia] = useState(false);
  const [mapaAberto, setMapaAberto] = useState(false);

  async function recarregarClientes() {
    const c = await lerClientes();
    if (c) setClientes(c);
  }

  useEffect(() => {
    void (async () => {
      const [cat, cli] = await Promise.all([lerCatalogo(), lerClientes()]);
      if (!cat) {
        setErro(true);
        return;
      }
      setCatalogo(cat);
      setClientes(cli ?? []);
    })();
  }, []);

  // As marcas do MÊS INTEIRO, de todos os clientes, numa consulta só. A versão
  // anterior lia por cliente, o que fazia 40 idas à rede num contador com 40
  // clientes — e deixava sem resposta a pergunta do dia 18: "quais dos meus
  // ainda não entregaram?".
  useEffect(() => {
    let vivo = true;
    void (async () => {
      const f = await lerFeitosDoMes(mesEmFoco);
      if (vivo && f) setFeitos(f);
    })();
    return () => {
      vivo = false;
    };
  }, [mesEmFoco]);

  const cliente = clientes.find((c) => c.id === clienteEmFoco) ?? null;

  // Um calendário por ano, com as exceções de feriado do banco. A grade de
  // janeiro mostra dias de dezembro, e ali o feriado é do ano anterior — então a
  // montagem cobre os três anos vizinhos.
  const calendarios = useMemo(() => {
    if (!catalogo) return null;
    const excecoes = {
      acrescentar: catalogo.feriadosExtras,
      remover: catalogo.feriadosRemovidos,
    };
    const mapa = new Map<number, ReturnType<typeof montarCalendario>>();
    for (const ano of [mesEmFoco.ano - 1, mesEmFoco.ano, mesEmFoco.ano + 1]) {
      mapa.set(ano, montarCalendario(ano, excecoes));
    }
    return mapa;
  }, [catalogo, mesEmFoco.ano]);

  const calDoAno = calendarios?.get(mesEmFoco.ano) ?? null;

  /** As obrigações que vencem no mês em foco, com data, derivação e decreto. */
  const doMes = useMemo(() => {
    if (!catalogo || !calDoAno) return [];
    const lista: { o: Obrigacao; v: Vencimento }[] = [];
    for (const o of catalogo.obrigacoes) {
      if (!o.ativa) continue;
      const indice = indexarExcecoes(catalogo.excecoes.get(o.id) ?? []);
      const v = vencimentoDaObrigacao(
        calDoAno,
        o.regra,
        o.periodicidade,
        mesEmFoco.ano,
        mesEmFoco.mes,
        indice,
      );
      if (!v.data) continue;
      lista.push({ o, v });
    }
    return lista.sort((a, b) =>
      a.v.data === b.v.data
        ? a.o.titulo.localeCompare(b.o.titulo, "pt-BR")
        : (a.v.data ?? "") < (b.v.data ?? "")
          ? -1
          : 1,
    );
  }, [catalogo, calDoAno, mesEmFoco]);

  /**
   * O filtro. Obrigação `geral` NUNCA é escondida pelo filtro de regime: ela
   * vale para todos, e sumir com ela faria o contador perder o DARF-INSS por ter
   * marcado "Simples Nacional". Quem decide isso é `lib/carteira.ts`, num lugar
   * só — aqui só se escolhe QUAL regime perguntar.
   */
  const filtradas = useMemo(() => {
    const regimesAtivos = cliente ? new Set<Regime>([cliente.regime]) : regimes;
    return doMes.filter(({ o }) => {
      if (setores.size > 0 && !setores.has(o.setor)) return false;
      if (regimesAtivos.size > 0 && !o.geral) {
        if (!o.regimes.some((r) => regimesAtivos.has(r))) return false;
      }
      return true;
    });
  }, [doMes, setores, regimes, cliente]);

  /** Os clientes que a carteira considera: todos, ou só o que está em foco. */
  const carteira = useMemo(
    () => (cliente ? [cliente] : clientes),
    [cliente, clientes],
  );

  const resumo = useMemo(
    () => resumoDaCarteira(filtradas.map(({ o }) => o), carteira, feitos),
    [filtradas, carteira, feitos],
  );

  const pendencias = useMemo(
    () => pendenciasDaCarteira(filtradas.map(({ o }) => o), carteira, feitos),
    [filtradas, carteira, feitos],
  );

  /** O próximo vencimento a partir de hoje, olhando ATRAVÉS do mês em foco. */
  const proximo = useMemo(() => {
    if (!catalogo || !calendarios) return null;
    for (let salto = 0; salto < 13; salto += 1) {
      const ano = mesEmFoco.ano + Math.floor((mesEmFoco.mes - 1 + salto) / 12);
      const mes = ((mesEmFoco.mes - 1 + salto) % 12) + 1;
      const cal = calendarios.get(ano) ?? montarCalendario(ano, {
        acrescentar: catalogo.feriadosExtras,
        remover: catalogo.feriadosRemovidos,
      });
      const candidatos: { o: Obrigacao; data: string }[] = [];
      for (const o of catalogo.obrigacoes) {
        if (!o.ativa) continue;
        const v = vencimentoDaObrigacao(
          cal,
          o.regra,
          o.periodicidade,
          ano,
          mes,
          indexarExcecoes(catalogo.excecoes.get(o.id) ?? []),
        );
        if (v.data && v.data >= hojeIso) candidatos.push({ o, data: v.data });
      }
      if (candidatos.length === 0) continue;
      candidatos.sort((a, b) => (a.data < b.data ? -1 : 1));
      const data = candidatos[0].data;
      const noDia = candidatos.filter((c) => c.data === data);
      // A contagem de dias sai de aritmética de string de data, não de
      // `Date.now()` diferença: as duas datas são 'AAAA-MM-DD' e o módulo puro já
      // sabe somar dias em UTC. E a DATA COMPLETA vai sempre ao lado do "em N
      // dias", porque o relógio do navegador não é fonte da verdade.
      const dias = Math.round(
        (Date.parse(`${data}T12:00:00Z`) - Date.parse(`${hojeIso}T12:00:00Z`)) / 86400000,
      );
      return { data, dias, quantos: noDia.length, titulo: noDia[0].o.titulo };
    }
    return null;
  }, [catalogo, calendarios, mesEmFoco, hojeIso]);

  const dias: DiaComObrigacoes[] = useMemo(() => {
    if (!calDoAno) return [];
    const porDia = new Map<string, { total: number; pendentes: number }>();
    for (const { o, v } of filtradas) {
      if (!v.data) continue;
      const atual = porDia.get(v.data) ?? { total: 0, pendentes: 0 };
      atual.total += 1;
      if (carteira.length > 0) {
        atual.pendentes += clientesQueDevem(o, carteira, feitos).length;
      }
      porDia.set(v.data, atual);
    }
    return gradeDoMes(calDoAno, mesEmFoco.ano, mesEmFoco.mes, hojeIso).map((c) => {
      const n = porDia.get(c.data);
      return {
        ...c,
        quantidade: n?.total ?? 0,
        pendentes: carteira.length > 0 ? (n?.pendentes ?? 0) : null,
      };
    });
  }, [calDoAno, mesEmFoco, filtradas, feitos, carteira, hojeIso]);

  const visiveis = diaEmFoco ? filtradas.filter(({ v }) => v.data === diaEmFoco) : filtradas;

  /** As linhas agrupadas por data — a data é dita UMA vez, na rubrica. */
  const grupos = useMemo(() => {
    const mapa = new Map<string, { o: Obrigacao; v: Vencimento }[]>();
    for (const item of visiveis) {
      const chave = item.v.data ?? "";
      mapa.set(chave, [...(mapa.get(chave) ?? []), item]);
    }
    return [...mapa.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [visiveis]);

  async function alternarUm(o: Obrigacao, clienteId: string) {
    setSalvando(`${clienteId}|${o.id}`);
    setFalhaAoMarcar(null);
    const querFeito = !feitos.has(chaveDoFeito(clienteId, o.id));
    const r = await marcar(clienteId, o.id, mesEmFoco, querFeito);
    setSalvando(null);
    if (!r.ok) {
      // Falha de marcação NÃO derruba a tela. A primeira versão fazia
      // `setErro(true)` aqui, e o efeito era brutal: um tique que não gravou —
      // rede oscilando, sessão expirando — trocava o mês inteiro pela mensagem
      // "não conseguimos carregar o calendário", apagando os prazos que o
      // contador estava lendo. Erro de LEITURA e erro de ESCRITA não têm o mesmo
      // tamanho: sem leitura não há tela; sem uma escrita, há tela e falta um
      // tique.
      setFalhaAoMarcar(o.titulo);
      return;
    }
    setFeitos((antes) => {
      const novo = new Set(antes);
      const k = chaveDoFeito(clienteId, o.id);
      if (querFeito) novo.add(k);
      else novo.delete(k);
      return novo;
    });
  }

  async function alternarLote(o: Obrigacao, feito: boolean) {
    const alvos = feito
      ? clientesQueDevem(o, carteira, feitos).map((c) => c.id)
      : clientesAlcancados(o, carteira)
          .filter((c) => feitos.has(chaveDoFeito(c.id, o.id)))
          .map((c) => c.id);
    if (alvos.length === 0) return;
    setSalvando(`lote|${o.id}`);
    setFalhaAoMarcar(null);
    const r = await marcarLote(o.id, mesEmFoco, alvos, feito);
    setSalvando(null);
    if (!r.ok) {
      setFalhaAoMarcar(o.titulo);
      return;
    }
    setFeitos((antes) => {
      const novo = new Set(antes);
      for (const id of alvos) {
        const k = chaveDoFeito(id, o.id);
        if (feito) novo.add(k);
        else novo.delete(k);
      }
      return novo;
    });
  }

  function limparFiltros() {
    setSetores(new Set());
    setRegimes(new Set());
    setDiaEmFoco(null);
    setClienteEmFoco("");
  }

  if (erro) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <p className="rounded-xl bg-superficie-2 p-6 text-center text-sm leading-relaxed text-cinza">
          Não conseguimos carregar o calendário agora. Recarregue a página; se continuar,
          fale com o suporte.
        </p>
      </main>
    );
  }
  if (!catalogo || !calDoAno) return <Carregando />;

  const naoConfirmadas = catalogo.obrigacoes.filter((o) => !o.regraConfirmada).length;
  const feriadosDoMes = [...calDoAno.feriados.values()]
    .filter((f) => f.data.slice(0, 7) === `${mesEmFoco.ano}-${String(mesEmFoco.mes).padStart(2, "0")}`)
    .sort((a, b) => (a.data < b.data ? -1 : 1));
  const temRecorte =
    setores.size > 0 || regimes.size > 0 || diaEmFoco !== null || clienteEmFoco !== "";
  const ajustadas = filtradas.filter(({ v }) => v.derivacao.ajustada).length;
  const emDiaNaoUtil = filtradas.filter(({ v }) => v.derivacao.emDiaNaoUtil).length;
  const nomeDoMes = NOMES_DOS_MESES[mesEmFoco.mes - 1];

  return (
    <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
      {/* O h1 fica em fundo CLARO de propósito. Ele é uma das três pernas da
          guarda "os dois temas deram números diferentes" na prova de tela — sobre
          navy daria 14,57 : 1 nos dois temas, e a guarda degeneraria em silêncio,
          voltando a medir a mesma coisa duas vezes. */}
      <header className="flex flex-col gap-4 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-titulo sm:text-3xl">
            Calendário Tributário Federal
          </h1>
          <p className="max-w-2xl pt-2 text-sm leading-relaxed text-cinza">
            Os prazos federais calculados pela regra de cada obrigação — inclusive o ajuste
            de fim de semana e feriado. Qualquer mês, de qualquer ano.
          </p>
        </div>
        <nav
          className="flex shrink-0 gap-1 border-b border-borda pb-px"
          aria-label="Seções da ferramenta"
        >
          {(
            [
              ["calendario", "Calendário"],
              ["clientes", `Meus clientes${clientes.length ? ` (${clientes.length})` : ""}`],
            ] as const
          ).map(([valor, rotulo]) => (
            <button
              key={valor}
              type="button"
              onClick={() => setAba(valor)}
              aria-current={aba === valor ? "page" : undefined}
              className={`-mb-px rounded-t-xl border-b-2 px-4 py-2.5 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/25 ${
                aba === valor
                  ? "border-laranja text-titulo"
                  : "border-transparent text-cinza hover:text-tinta"
              }`}
            >
              {rotulo}
            </button>
          ))}
        </nav>
      </header>

      {aba === "clientes" ? (
        <Clientes clientes={clientes} onMudou={recarregarClientes} />
      ) : (
        <>
          {/* ============================================================
              A CABINE — o único território navy da tela.
              Card arredondado, e não faixa de largura total: o `Topo` do hub já
              é `sticky bg-azul` chapado, e faixa navy encostada nele empilha
              dois territórios azuis e come meia tela em viewport curto.
              `colorScheme: dark` afirma um FATO invariante (esta superfície é
              escura nos dois temas) e é o que faz a lista suspensa dos selects
              sair legível no Windows — não é `dark:` disfarçado, que assumiria
              qual tema está ativo.
              ============================================================ */}
          <section
            aria-label={`Cabine de ${nomeDoMes} de ${mesEmFoco.ano}`}
            className="painel-navy rounded-cf-lg p-4 sm:p-6"
            style={{ colorScheme: "dark" }}
          >
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-7">
              {/* A ordem no DOM é resumo → grade (ordem de leitura e do celular);
                  `lg:order-*` só troca a posição visual no desktop. */}
              <div className="flex min-w-0 flex-col gap-4 lg:order-2 lg:flex-1">
                <NavegacaoDeMes
                  ano={mesEmFoco.ano}
                  mes={mesEmFoco.mes}
                  onAnterior={() => {
                    setMesEmFoco(mesAnterior(mesEmFoco.ano, mesEmFoco.mes));
                    setDiaEmFoco(null);
                  }}
                  onSeguinte={() => {
                    setMesEmFoco(mesSeguinte(mesEmFoco.ano, mesEmFoco.mes));
                    setDiaEmFoco(null);
                  }}
                  onHoje={() => {
                    setMesEmFoco({ ano: hoje.getFullYear(), mes: hoje.getMonth() + 1 });
                    setDiaEmFoco(null);
                  }}
                  onIrPara={(ano, mes) => {
                    setMesEmFoco({ ano, mes });
                    setDiaEmFoco(null);
                  }}
                />

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <TileDeFato
                    rotulo="Prazos no mês"
                    valor={filtradas.length}
                    apoio={`em ${grupos.length} ${grupos.length === 1 ? "data" : "datas"}`}
                  />
                  <TileDeFato
                    rotulo="Próximo"
                    valor={
                      proximo
                        ? proximo.dias === 0
                          ? "hoje"
                          : proximo.dias === 1
                            ? "amanhã"
                            : `${proximo.dias} dias`
                        : "—"
                    }
                    apoio={
                      proximo
                        ? `${dataBr(proximo.data)} · ${
                            proximo.quantos === 1 ? proximo.titulo : `${proximo.quantos} prazos`
                          }`
                        : "nada à frente no catálogo"
                    }
                  />
                  <TileDeFato
                    className="col-span-2 sm:col-span-1"
                    rotulo="Minha carteira"
                    valor={
                      carteira.length === 0
                        ? "—"
                        : `${resumo.entregues}/${resumo.pares}`
                    }
                    apoio={
                      carteira.length === 0
                        ? "nenhum cliente cadastrado"
                        : resumo.comPendencia === 0
                          ? "toda a carteira entregou"
                          : `${resumo.comPendencia} ${
                              resumo.comPendencia === 1 ? "cliente com pendência" : "clientes com pendência"
                            }`
                    }
                  />
                </div>

                {/* A frase do mês: a tese em uma linha, e um controle dentro. Os
                    números saem da MESMA lista que a tela desenha (`filtradas`),
                    não de uma contagem paralela. */}
                <p className="text-xs leading-relaxed text-navy-mute">
                  {ajustadas === 0 && emDiaNaoUtil === 0 ? (
                    <>
                      Nenhum prazo de {nomeDoMes.toLowerCase()} caiu em fim de semana ou
                      feriado — todos vencem no dia da regra.
                    </>
                  ) : (
                    <>
                      {ajustadas > 0 && (
                        <>
                          <strong className="font-semibold text-white">
                            {ajustadas}{" "}
                            {ajustadas === 1 ? "prazo teve a data ajustada" : "prazos tiveram a data ajustada"}
                          </strong>{" "}
                          porque o dia da regra caiu em dia não útil
                        </>
                      )}
                      {ajustadas > 0 && emDiaNaoUtil > 0 && ", e "}
                      {emDiaNaoUtil > 0 && (
                        <>
                          <strong className="font-semibold text-laranja-claro">
                            {emDiaNaoUtil}{" "}
                            {emDiaNaoUtil === 1 ? "vence" : "vencem"} em dia não útil
                          </strong>{" "}
                          mesmo assim, porque a regra dele não ajusta
                        </>
                      )}
                      .
                    </>
                  )}
                </p>

                {feriadosDoMes.length > 0 && (
                  <div>
                    <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.09em] text-navy-mute">
                      Feriados de {nomeDoMes.toLowerCase()}
                    </p>
                    <ul className="flex flex-col gap-0.5 pt-1">
                      {feriadosDoMes.map((f) => (
                        <li key={f.data} className="text-xs text-white">
                          <span className="font-semibold">{dataBr(f.data).slice(0, 5)}</span>{" "}
                          <span className="text-navy-mute">{f.nome}</span>
                        </li>
                      ))}
                    </ul>
                    {/* Esta frase mora AQUI, ao lado dos feriados que ela
                        explica, e não no pé da página: é a única superfície em
                        que um humano confere que Corpus Christi não está na
                        lista. */}
                    <p className="pt-1.5 text-[0.6875rem] leading-relaxed text-navy-mute">
                      Carnaval e Corpus Christi não entram: são ponto facultativo, não
                      feriado nacional.
                    </p>
                  </div>
                )}
              </div>

              {/* No CELULAR a grade começa recolhida, e isto é medição: ela ocupa
                  304px dos 706px da cabine em 390px de largura, para marcar cinco
                  dias — 82% das células estão vazias, e a lista com as rubricas
                  está logo abaixo, dando a mesma leitura de "onde o mês pesa". No
                  desktop ela é sempre visível, porque ali o espaço é lateral e não
                  disputa com nada.
                  Ela continua NO DOM nos dois casos (`hidden`, não desmontada):
                  desmontar faria a contagem de 42 células virar zero na largura
                  pequena, e a prova mediria a ausência sem reclamar. */}
              <div className="lg:order-1 lg:w-[21.5rem] lg:shrink-0">
                <button
                  type="button"
                  onClick={() => setMapaAberto((v) => !v)}
                  aria-expanded={mapaAberto}
                  className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-navy-mute/25 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white transition hover:border-laranja lg:hidden"
                >
                  {mapaAberto ? "Esconder a grade do mês" : "Ver a grade do mês"}
                  <span aria-hidden="true">{mapaAberto ? "▴" : "▾"}</span>
                </button>
                <div className={mapaAberto ? "" : "hidden lg:block"}>
                  <GradeDoMes
                    dias={dias}
                    selecionado={diaEmFoco}
                    onSelecionar={(d) => setDiaEmFoco((atual) => (atual === d ? null : d))}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* A nota cavalga a borda inferior do navy — o gesto de `Inicio.tsx`,
              onde o card de senha sobe `-mt-14` sobre o hero. */}
          {naoConfirmadas > 0 && (
            <aside className="surface relative z-10 -mt-6 border-l-[3px] border-l-laranja p-5 sm:p-6">
              <h2 className="text-base font-semibold text-titulo">
                Antes de usar como prazo final
              </h2>
              <p className="max-w-[68ch] pt-1.5 text-sm leading-relaxed text-cinza">
                <strong className="text-tinta">
                  {naoConfirmadas} de {catalogo.obrigacoes.length} regras ainda não foram
                  conferidas na norma
                </strong>{" "}
                pela equipe da Contabilidade Facilitada — elas foram deduzidas dos prazos que
                a ferramenta antiga mostrava, e estão marcadas na lista. Confira a legislação
                antes de usar como prazo final.
              </p>
            </aside>
          )}

          {catalogo.ilegiveis > 0 && (
            <p className="mt-4 rounded-xl bg-erro-fundo p-4 text-sm leading-relaxed text-erro-tinta">
              {catalogo.ilegiveis}{" "}
              {catalogo.ilegiveis === 1 ? "obrigação está" : "obrigações estão"} com a regra em
              formato que esta tela não sabe ler, e por isso não aparecem. Avise o suporte — é
              defeito do catálogo, não do seu acesso.
            </p>
          )}

          {/* ============================================================
              A BARRA DE FILTROS — grudada abaixo do Topo.
              `top-[var(--cf-topo)]`, e a variável é MEDIDA (`py-3` em volta de
              `min-h-11` = 68px). Cravar 56 ou 72 à mão deixa a barra enterrada
              atrás do topo ou flutuando com um vão.
              Some o card `rounded-2xl bg-superficie p-4`: era ele que fazia a
              tela parecer formulário.
              ============================================================ */}
          <section
            role="group"
            aria-labelledby="filtros-do-calendario"
            className="sticky top-[var(--cf-topo)] z-20 mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 border-y border-borda bg-fundo/95 py-3 backdrop-blur"
          >
            <h2 id="filtros-do-calendario" className="sr-only">
              Filtros
            </h2>
            <ChipsDeFiltro
              id="filtro-setor"
              rotulo="Setor"
              opcoes={SETORES.map((s) => ({
                ...s,
                setor: s.valor,
                quantos: doMes.filter(({ o }) => o.setor === s.valor).length,
              }))}
              ativos={setores}
              onAlternar={(v) =>
                setSetores((a) => {
                  const n = new Set(a);
                  if (n.has(v)) n.delete(v);
                  else n.add(v);
                  return n;
                })
              }
            />
            <span className="hidden h-6 w-px bg-borda sm:block" aria-hidden="true" />
            {cliente ? (
              <p className="text-xs text-cinza">
                Regime:{" "}
                <span className="font-semibold text-tinta">{rotuloDoRegime(cliente.regime)}</span>{" "}
                — do cliente em foco
              </p>
            ) : (
              <ChipsDeFiltro
                id="filtro-regime"
                rotulo="Regime"
                opcoes={REGIMES.map((r) => ({ valor: r.valor, rotulo: r.curto }))}
                ativos={regimes}
                onAlternar={(v) =>
                  setRegimes((a) => {
                    const n = new Set(a);
                    if (n.has(v)) n.delete(v);
                    else n.add(v);
                    return n;
                  })
                }
              />
            )}
            <label className="ml-auto flex items-center gap-2 text-xs">
              <span className="font-semibold uppercase tracking-wide text-cinza">
                Ver só um cliente
              </span>
              <select
                value={clienteEmFoco}
                onChange={(e) => setClienteEmFoco(e.target.value)}
                className="h-10 max-w-[13rem] rounded-full border-[1.5px] border-borda bg-superficie px-3 text-sm text-tinta transition focus:border-laranja focus:ring-3 focus:ring-laranja/20 focus:outline-none"
              >
                <option value="">Nenhum — toda a carteira</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome} · {rotuloDoRegime(c.regime)}
                  </option>
                ))}
              </select>
            </label>
            {temRecorte && (
              <button
                type="button"
                onClick={limparFiltros}
                className="text-xs font-semibold text-link underline"
              >
                limpar filtros
              </button>
            )}
          </section>

          {/* ============================================================
              A CARTEIRA — quem ainda deve, em cartões
              ============================================================ */}
          {clientes.length > 0 && (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-3 pt-7 pb-3">
                <h2 id="carteira" className="text-sm font-semibold text-titulo">
                  Minha carteira em {nomeDoMes.toLowerCase()}{" "}
                  <span className="font-normal text-cinza">
                    · {carteira.length} {carteira.length === 1 ? "cliente" : "clientes"}
                  </span>
                </h2>
              </div>
              <CarteiraEmCartoes
                pendencias={pendencias}
                inteira={carteiraInteira}
                mostrandoEmDia={mostrandoEmDia}
                onVerTodos={() => setCarteiraInteira(true)}
                onVerEmDia={() => setMostrandoEmDia((v) => !v)}
                onEscolher={(id) => setClienteEmFoco(id)}
                nomeDoMes={nomeDoMes}
              />
            </>
          )}

          {clientes.length === 0 && (
            <aside className="mt-7 rounded-cf-lg border border-dashed border-borda p-5">
              <h2 className="text-sm font-semibold text-titulo">
                Cadastre seus clientes para marcar o que já foi entregue
              </h2>
              <p className="max-w-[68ch] pt-1.5 text-sm leading-relaxed text-cinza">
                Com eles cadastrados, cada prazo mostra os clientes que aquela obrigação
                alcança e você marca todos de uma vez. Só você vê essa lista, e nada é
                enviado a ninguém.
              </p>
              <button
                type="button"
                onClick={() => setAba("clientes")}
                className="pill-cta mt-4 text-sm"
              >
                Cadastrar clientes
              </button>
            </aside>
          )}

          {/* ============================================================
              OS PRAZOS — agrupados por DATA, a data dita uma vez
              ============================================================ */}
          <div
            ref={prazosRef}
            className="flex flex-wrap items-baseline justify-between gap-3 pt-8 pb-1"
          >
            <h2 className="text-sm font-semibold text-titulo">
              {diaEmFoco ? `Prazos de ${dataBr(diaEmFoco)}` : `Prazos de ${nomeDoMes.toLowerCase()}`}
              <span className="pl-1.5 font-normal text-cinza">
                ({visiveis.length}
                {carteira.length > 0 && resumo.pares > 0
                  ? ` · ${resumo.entregues} de ${resumo.pares} entregues`
                  : ""}
                )
              </span>
            </h2>
            {diaEmFoco && (
              <button
                type="button"
                onClick={() => setDiaEmFoco(null)}
                className="inline-flex items-center gap-1.5 rounded-full bg-chip px-2.5 py-1 text-xs font-semibold text-chip-tinta transition hover:bg-laranja/20"
              >
                {dataBr(diaEmFoco).slice(0, 5)} <span aria-hidden="true">✕</span>
                <span className="sr-only">tirar o recorte por dia</span>
              </button>
            )}
          </div>

          {falhaAoMarcar && (
            <p
              role="status"
              className="mb-2 rounded-xl bg-erro-fundo p-3 text-xs leading-relaxed text-erro-tinta"
            >
              Não conseguimos gravar a marca de <strong>{falhaAoMarcar}</strong>. Os prazos
              continuam aqui — tente marcar de novo em instantes.
            </p>
          )}

          {grupos.length === 0 ? (
            <p className="rounded-xl bg-superficie-2 p-5 text-sm leading-relaxed text-cinza">
              {temRecorte
                ? "Nenhum prazo com estes filtros. Tire um filtro para ver o resto do mês."
                : `Nenhuma obrigação vence em ${nomeDoMes.toLowerCase()} de ${mesEmFoco.ano}.`}
            </p>
          ) : (
            grupos.map(([data, itens]) => (
              <section
                key={data}
                data-grupo={data}
                aria-labelledby={`dia-${data}`}
                className="border-t border-borda first:border-t-0"
              >
                <RubricaDoDia
                  data={data}
                  quantos={itens.length}
                  ehDiaUtil={ehDiaUtil(calDoAno, data)}
                  jaPassou={data < hojeIso}
                />
                <ul className="flex flex-col gap-2 pb-5">
                  {itens.map(({ o, v }) => (
                    <LinhaDoPrazo
                      key={o.id}
                      obrigacao={o}
                      vencimento={v}
                      carteira={carteira}
                      feitos={feitos}
                      salvando={salvando}
                      onAlternarUm={alternarUm}
                      onAlternarLote={alternarLote}
                    />
                  ))}
                </ul>
              </section>
            ))
          )}

          <footer className="mt-8 rounded-cf-lg bg-superficie-2 p-5">
            <h2 className="text-sm font-semibold text-titulo">De onde vêm estas datas</h2>
            <p className="pt-2 text-sm leading-relaxed text-cinza">
              Cada obrigação guarda a <strong className="text-tinta">regra</strong> — dia fixo
              com ajuste, n-ésimo dia útil ou último dia útil — e a data é calculada dela, com
              os feriados nacionais em lei. É isso que faz o calendário valer para qualquer
              mês de qualquer ano, e é isso que o selo{" "}
              <span className="font-mono text-xs text-tinta">sáb 20 → seg 22</span> mostra:
              onde o dia da regra caiu em dia não útil e para onde o prazo foi.
            </p>
          </footer>
        </>
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// A tira da carteira
// ---------------------------------------------------------------------------
/**
 * Os clientes com pendência, em cartões — no máximo seis.
 *
 * O corte em seis é decisão de ESCALA, não de gosto: com 40 clientes, 40 cartões
 * de 11,5rem dariam ~7.400px de rolagem lateral, e rolagem horizontal longa
 * esconde o fim dela. "Ver todos os N" abre em `flex-wrap`, sem rolar.
 *
 * Não é `ul/li`: um `li` aqui volta a competir com a lista de prazos em qualquer
 * seletor global futuro — a cicatriz dos sete rótulos iguais da calculadora, onde
 * a prova escrevia num checkbox achado por texto sem escopo.
 */
function CarteiraEmCartoes({
  pendencias,
  inteira,
  mostrandoEmDia,
  onVerTodos,
  onVerEmDia,
  onEscolher,
  nomeDoMes,
}: {
  pendencias: ReturnType<typeof pendenciasDaCarteira>;
  inteira: boolean;
  mostrandoEmDia: boolean;
  onVerTodos: () => void;
  onVerEmDia: () => void;
  onEscolher: (id: string) => void;
  nomeDoMes: string;
}) {
  const devendo = pendencias.filter((p) => p.devem > 0);
  const emDia = pendencias.filter((p) => p.devem === 0);
  const mostrados = inteira ? devendo : devendo.slice(0, 6);

  if (devendo.length === 0) {
    return (
      <p className="rounded-xl bg-aviso-fundo p-4 text-sm font-medium text-aviso-tinta">
        Toda a carteira entregou os prazos de {nomeDoMes.toLowerCase()}.
      </p>
    );
  }

  return (
    <div>
      <div
        role="group"
        aria-labelledby="carteira"
        className={`flex gap-2 pb-1 ${inteira ? "flex-wrap" : "snap-x overflow-x-auto"}`}
      >
        {mostrados.map((p) => (
          <button
            key={p.cliente.id}
            type="button"
            onClick={() => onEscolher(p.cliente.id)}
            className="surface snap-start min-w-[11.5rem] shrink-0 p-3 text-left transition hover:border-laranja focus-visible:border-laranja focus-visible:outline-none"
          >
            <p className="truncate text-sm font-semibold text-tinta">{p.cliente.nome}</p>
            <p className="pt-0.5 text-xs text-cinza">
              <span className="font-display text-base font-bold text-laranja-texto">
                {p.devem}
              </span>{" "}
              de {p.alcancam} a entregar
            </p>
          </button>
        ))}
        {!inteira && devendo.length > 6 && (
          <button
            type="button"
            onClick={onVerTodos}
            className="btn-contorno snap-start shrink-0 text-xs"
          >
            ver todos os {devendo.length}
          </button>
        )}
      </div>
      {emDia.length > 0 && (
        <div className="pt-2">
          <button
            type="button"
            onClick={onVerEmDia}
            className="text-xs font-semibold text-sucesso underline"
          >
            {emDia.length} {emDia.length === 1 ? "cliente em dia" : "clientes em dia"}
            {mostrandoEmDia ? " (esconder)" : ""}
          </button>
          {mostrandoEmDia && (
            <p className="pt-1.5 text-xs leading-relaxed text-cinza">
              {emDia.map((p) => p.cliente.nome).join(" · ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// A linha de um prazo, com a fila de clientes dentro
// ---------------------------------------------------------------------------
/**
 * A obrigação é a LINHA; os clientes que ela alcança são caixas DENTRO dela.
 *
 * Esta é a inversão que muda o trabalho. Antes, a marca de feito exigia escolher
 * um "cliente em foco", então fechar o mês para 12 clientes custava 12 trocas de
 * modo e 12 leituras de rede. Agora "marcar os 12 do Simples" é um clique — e uma
 * transação (`calendario_marcar_lote`, 000112), não doze chamadas: doze idas com
 * a sétima caindo deixaria seis marcas gravadas e exigiria doze cliques para
 * desfazer, e num checklist em que o contador aposta multa isso é motivo para não
 * confiar na marca.
 *
 * A marca continua sendo sempre DE alguém: o nome do cliente está escrito na
 * caixa e no `aria-label`.
 */
function LinhaDoPrazo({
  obrigacao: o,
  vencimento: v,
  carteira,
  feitos,
  salvando,
  onAlternarUm,
  onAlternarLote,
}: {
  obrigacao: Obrigacao;
  vencimento: Vencimento;
  carteira: Cliente[];
  feitos: Set<string>;
  salvando: string | null;
  onAlternarUm: (o: Obrigacao, clienteId: string) => void;
  onAlternarLote: (o: Obrigacao, feito: boolean) => void;
}) {
  const alcancados = clientesAlcancados(o, carteira);
  const devem = alcancados.filter((c) => !feitos.has(chaveDoFeito(c.id, o.id)));
  const todosFeitos = alcancados.length > 0 && devem.length === 0;
  const noLote = salvando === `lote|${o.id}`;

  return (
    <li
      data-prova="linha"
      className={`rounded-cf border-[1.5px] p-3 transition ${
        todosFeitos ? "border-borda bg-superficie-2" : "border-borda bg-superficie"
      }`}
    >
      {/* UMA faixa para tudo que é rótulo: ponto de setor, título, selo do
          ajuste e etiquetas. Pôr as etiquetas de regime numa linha própria custava
          ~32px em cada uma das 14 linhas — 450px de página para repetir o que já
          está na primeira faixa. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <PontoDeSetor setor={o.setor} />
        <h4
          className={`min-w-0 flex-1 text-sm font-semibold ${
            todosFeitos ? "text-cinza" : "text-tinta"
          }`}
        >
          {o.titulo}
        </h4>
        <SeloDeAjuste vencimento={v} />
        {v.excecao && <Etiqueta tom="alerta">prazo alterado por norma</Etiqueta>}
        {/* Com um cliente único em foco o regime já é conhecido — repeti-lo em
            cada linha é ruído. */}
        {carteira.length !== 1 && <EtiquetasDeRegime geral={o.geral} regimes={o.regimes} />}
        {!o.regraConfirmada && <Etiqueta tom="alerta">regra não conferida</Etiqueta>}
      </div>

      {/* A explicação e a descrição num parágrafo só, sempre visível: sem clamp e
          sem `<details>`. Clamp deixa o texto no `innerText` e FORA DO OLHO — a
          asserção ficaria verde com o conteúdo invisível, que é uma classe nova
          de verde falso. E este é um produto de ensino: a descrição é o que diz o
          que a obrigação é. */}
      <p className="pt-1 text-xs leading-relaxed text-cinza">
        {v.excecao ? v.excecao.motivo : explicarRegra(o.regra, o.periodicidade)}
        {o.descricao && <> · {o.descricao}</>}
      </p>

      {alcancados.length > 0 && (
        <div className="mt-2.5 border-t border-borda pt-2.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-[0.6875rem] font-semibold uppercase tracking-wide text-cinza">
              {todosFeitos
                ? `entregue para ${alcancados.length}`
                : `falta para ${devem.length} de ${alcancados.length}`}
            </span>
            {devem.length > 0 && (
              <button
                type="button"
                onClick={() => onAlternarLote(o, true)}
                disabled={noLote}
                className="rounded-full bg-laranja px-3 py-1 text-xs font-semibold text-azul transition hover:brightness-105 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/40"
              >
                {noLote ? "marcando…" : `marcar ${devem.length}`}
              </button>
            )}
            {todosFeitos && (
              <button
                type="button"
                onClick={() => onAlternarLote(o, false)}
                disabled={noLote}
                className="rounded-full border border-borda px-3 py-1 text-xs font-medium text-cinza transition hover:border-laranja disabled:opacity-60"
              >
                {noLote ? "desmarcando…" : "desmarcar todos"}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 pt-2">
            {alcancados.map((c) => {
              const feito = feitos.has(chaveDoFeito(c.id, o.id));
              const nesta = salvando === `${c.id}|${o.id}`;
              return (
                <label
                  key={c.id}
                  className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                    feito
                      ? "border-aviso-tinta/30 bg-aviso-fundo text-aviso-tinta"
                      : "border-borda bg-superficie text-cinza hover:border-laranja"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={feito}
                    disabled={nesta || noLote}
                    onChange={() => onAlternarUm(o, c.id)}
                    className="size-3.5 accent-laranja"
                  />
                  <span className="max-w-[10rem] truncate font-medium">{c.nome}</span>
                  <span className="sr-only">
                    {feito ? "Desmarcar" : "Marcar"} {o.titulo} como entregue para {c.nome}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </li>
  );
}
